import React, { useState, useEffect } from "react";
import {
  useNavigate,
  useParams,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import {
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineXMark,
  HiOutlineCheckCircle,
  HiOutlineClock,
} from "react-icons/hi2";
import {
  authService,
  workOrderService,
  masterDataService,
  machineService,
  plantService,
  lineService,
  userService,
  sparePartsService,
} from "../services";
import WorkOrderStep1BasicInfo from "./workorder/WorkOrderStep1BasicInfo";
import WorkOrderStep2Equipment from "./workorder/WorkOrderStep2Equipment";
import WorkOrderStep3Description from "./workorder/WorkOrderStep3Description";
import WorkOrderStep4Scheduling from "./workorder/WorkOrderStep4Scheduling";
import WorkOrderStep5Resources from "./workorder/WorkOrderStep5Resources";
import WorkOrderStep6Safety from "./workorder/WorkOrderStep6Safety";
import WorkOrderStep7Execution from "./workorder/WorkOrderStep7Execution";
import WorkOrderStep8Costs from "./workorder/WorkOrderStep8Costs";
import WorkOrderStep9Completion from "./workorder/WorkOrderStep9Completion";
import { toast } from "react-toastify";
import PageLoading from "../components/common/PageLoading";
import "./WorkOrderWizard.css";

const TOTAL_STEPS = 9;

const WorkOrderWizard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const breakdownLogId = searchParams.get("breakdownLogId");

  const tenantId = authService.getTenantId();
  const currentUser = authService.getUser();
  const isEditMode = !!id;

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Master data
  const [workOrderTypes, setWorkOrderTypes] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [machines, setMachines] = useState([]);
  const [plants, setPlants] = useState([]);
  const [lines, setLines] = useState([]);
  const [users, setUsers] = useState([]);
  const [rootCauseCategories, setRootCauseCategories] = useState([]);
  const [failureModes, setFailureModes] = useState([]);
  const [spareParts, setSpareParts] = useState([]);
  const [tools, setTools] = useState([]);
  const [ppeList, setPpeList] = useState([]);
  const [maintenanceWindows, setMaintenanceWindows] = useState([]);

  // Form data - comprehensive state for all steps
  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    workOrderNumber: "",
    statusId: "",
    priorityId: "",
    workTypeId: "",

    // Step 2: Equipment
    machineId: "",
    plantId: "",
    lineId: "",
    equipmentCode: "",
    assetCategory: "",
    serialNumber: "",

    // Step 3: Description
    problemDescription: "",
    taskDescription: "",
    rootCauseCategoryId: "",
    failureModeId: "",
    attachmentsJson: "",

    // Step 4: Scheduling
    scheduledStartDate: "",
    scheduledEndDate: "",
    scheduledStartTime: "",
    scheduledEndTime: "",
    estimatedDurationHours: "",
    maintenanceWindowId: "",
    isPlanned: false,

    // Step 5: Resources
    assignedTechnicianIds: [],
    skillCategory: "",
    spareParts: [],
    toolsRequired: [],
    ppeRequired: [],

    // Step 6: Safety
    lockoutTagoutRequired: false,
    safetyInstructions: "",
    riskLevel: "",

    // Step 7: Execution
    actualStartDate: "",
    actualEndDate: "",
    actualStartTime: "",
    actualEndTime: "",
    actualDurationHours: "",
    downtimeMinutes: "",
    technicianNotes: "",
    observations: "",
    additionalIssues: "",
    readingsJson: "",

    // Step 8: Costs
    laborHours: "",
    laborCost: "",
    partsCost: "",
    otherCost: "",
    totalCost: "",

    // Step 9: Completion
    completedBy: "",
    technicianSignature: "",
    verifiedBy: "",
    closedDate: "",
    maintenanceCode: "",

    // Legacy fields
    title: "",
    description: "",
    requestedBy: currentUser?.userId || 0,
    assignedTo: "",
    priority: "Medium",
    workOrderType: "Breakdown",
    breakdownLogId: breakdownLogId || null,
  });

  const [formErrors, setFormErrors] = useState({});
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [existingWorkOrderData, setExistingWorkOrderData] = useState(null);

  useEffect(() => {
    if (tenantId) {
      // Load cached data immediately if available
      const cachedTypes = sessionStorage.getItem("work_order_types");
      const cachedPriorities = sessionStorage.getItem("work_order_priorities");
      const cachedStatuses = sessionStorage.getItem("work_order_statuses");
      const cachedMachines = sessionStorage.getItem("machines_data");
      const cachedPlants = sessionStorage.getItem("plants_data");
      const cachedLines = sessionStorage.getItem("lines_data");
      const cachedUsers = sessionStorage.getItem("users_data");
      const cachedRootCauses = sessionStorage.getItem("root_cause_categories");
      const cachedFailureModes = sessionStorage.getItem("failure_modes");
      const cachedSpareParts = sessionStorage.getItem("spare_parts_data");
      const cachedTools = sessionStorage.getItem("tools_data");
      const cachedPPE = sessionStorage.getItem("ppe_data");
      const cachedWindows = sessionStorage.getItem("maintenance_windows");

      if (cachedTypes) setWorkOrderTypes(JSON.parse(cachedTypes));
      if (cachedPriorities) setPriorities(JSON.parse(cachedPriorities));
      if (cachedStatuses) setStatuses(JSON.parse(cachedStatuses));
      if (cachedMachines) setMachines(JSON.parse(cachedMachines));
      if (cachedPlants) setPlants(JSON.parse(cachedPlants));
      if (cachedLines) setLines(JSON.parse(cachedLines));
      if (cachedUsers) setUsers(JSON.parse(cachedUsers));
      if (cachedRootCauses)
        setRootCauseCategories(JSON.parse(cachedRootCauses));
      if (cachedFailureModes) setFailureModes(JSON.parse(cachedFailureModes));
      if (cachedSpareParts) setSpareParts(JSON.parse(cachedSpareParts));
      if (cachedTools) setTools(JSON.parse(cachedTools));
      if (cachedPPE) setPpeList(JSON.parse(cachedPPE));
      if (cachedWindows) setMaintenanceWindows(JSON.parse(cachedWindows));

      // If we have most of the cached data, don't show loading spinner
      const hasMostCachedData =
        cachedTypes &&
        cachedPriorities &&
        cachedStatuses &&
        cachedMachines &&
        cachedPlants &&
        cachedUsers;
      if (hasMostCachedData) {
        setLoading(false);
      }

      // Load fresh data in background
      loadMasterData();

      if (isEditMode) {
        loadWorkOrder();
      } else if (breakdownLogId) {
        loadBreakdownData();
      } else if (location.state) {
        // Handle navigation from OEE Logs or other pages
        const { machineId, plantId, description } = location.state;

        // Auto-generate Work Order Number
        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, "0");
        const dd = String(now.getDate()).padStart(2, "0");
        const dateStr = `${yyyy}${mm}${dd}`;
        const randomSuffix = Math.floor(Math.random() * 10000)
          .toString()
          .padStart(4, "0");

        const autoGeneratedNumber = `WO-${tenantId}-${dateStr}-${randomSuffix}`;

        setFormData((prev) => ({
          ...prev,
          machineId: machineId ? machineId.toString() : prev.machineId,
          plantId: plantId ? plantId.toString() : prev.plantId,
          problemDescription: description || prev.problemDescription,
          workOrderNumber: autoGeneratedNumber,
          // Set default status to Open or Draft if available usually handled by backend but good to have a default
          statusId:
            statuses.find(
              (s) => s.statusName === "Open" || s.statusName === "Draft"
            )?.statusId || prev.statusId,
        }));
      } else {
        // Auto-generate Work Order Number for new empty forms too
        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, "0");
        const dd = String(now.getDate()).padStart(2, "0");
        const dateStr = `${yyyy}${mm}${dd}`;
        const randomSuffix = Math.floor(Math.random() * 10000)
          .toString()
          .padStart(4, "0");

        const autoGeneratedNumber = `WO-${tenantId}-${dateStr}-${randomSuffix}`;

        setFormData((prev) => ({
          ...prev,
          workOrderNumber: autoGeneratedNumber,
        }));
      }
    } else {
      setError("Please login to manage work orders");
      setLoading(false);
    }
  }, [tenantId, id, breakdownLogId, location.state]);

  const loadMasterData = async () => {
    try {
      // Only show loading if we don't have cached data
      const hasCachedData = sessionStorage.getItem("work_order_types");
      if (!hasCachedData) {
        setLoading(true);
      }

      const [
        typesData,
        prioritiesData,
        statusesData,
        machinesData,
        plantsData,
        linesData,
        usersData,
        rootCausesData,
        failureModesData,
        sparePartsData,
        toolsData,
        ppeData,
        windowsData,
      ] = await Promise.all([
        masterDataService.getWorkOrderTypes(tenantId).catch(() => []),
        masterDataService.getWorkOrderPriorities(tenantId).catch(() => []),
        masterDataService
          .getStatuses(tenantId)
          .then((s) => {
            if (!s || s.length === 0) return [];
            // Filter for Action statuses, but also include statuses with null/undefined statusType
            // to handle cases where statuses might not have been properly categorized for new tenants
            // Common work order status names to include even if statusType is missing
            const workOrderStatusNames = ['Pending', 'In Progress', 'Completed', 'Cancelled', 'Draft', 'Open', 'Closed'];
            return s.filter((st) => {
              // Include if statusType is Action (standard work order statuses)
              if (st.statusType === "Action") return true;
              // Include if statusType is null/undefined/empty AND it's a known work order status name
              // This handles cases where statuses for new tenants might not have statusType set
              if (!st.statusType || st.statusType === null || st.statusType === undefined || st.statusType === "") {
                return workOrderStatusNames.some(name =>
                  st.statusName && st.statusName.toLowerCase() === name.toLowerCase()
                );
              }
              return false;
            });
          })
          .catch(() => []),
        machineService.getAllMachines(tenantId).catch(() => []),
        plantService.getAllPlants(tenantId).catch(() => []),
        lineService.getAllLines(tenantId).catch(() => []),
        userService.getAllUsers(tenantId).catch(() => []),
        masterDataService.getRootCauseCategories(tenantId).catch(() => []),
        masterDataService.getFailureModes(tenantId).catch(() => []),
        sparePartsService.getParts().catch(() => []),
        masterDataService.getTools(tenantId).catch(() => []),
        masterDataService.getPPEList(tenantId).catch(() => []),
        masterDataService.getMaintenanceWindows(tenantId).catch(() => []),
      ]);

      const pData = plantsData || [];
      setPlants(pData);

      // Auto-select plant if only 1 exists (for new entries)
      if (!isEditMode && pData.length === 1 && !formData.plantId) {
          setFormData(prev => ({ ...prev, plantId: pData[0].plantId?.toString() }));
      }
      setWorkOrderTypes(typesData || []);
      setPriorities(prioritiesData || []);
      setStatuses(statusesData || []);
      setMachines(machinesData || []);
      setPlants(plantsData || []);
      setLines(linesData || []);
      setUsers(usersData || []);
      setRootCauseCategories(rootCausesData || []);
      setFailureModes(failureModesData || []);
      setSpareParts(sparePartsData || []);
      setTools(toolsData || []);
      setPpeList(ppeData || []);
      setMaintenanceWindows(windowsData || []);

      // Cache all the data
      sessionStorage.setItem(
        "work_order_types",
        JSON.stringify(typesData || [])
      );
      sessionStorage.setItem(
        "work_order_priorities",
        JSON.stringify(prioritiesData || [])
      );
      sessionStorage.setItem(
        "work_order_statuses",
        JSON.stringify(statusesData || [])
      );
      sessionStorage.setItem(
        "machines_data",
        JSON.stringify(machinesData || [])
      );
      sessionStorage.setItem("plants_data", JSON.stringify(plantsData || []));
      sessionStorage.setItem("lines_data", JSON.stringify(linesData || []));
      sessionStorage.setItem("users_data", JSON.stringify(usersData || []));
      sessionStorage.setItem(
        "root_cause_categories",
        JSON.stringify(rootCausesData || [])
      );
      sessionStorage.setItem(
        "failure_modes",
        JSON.stringify(failureModesData || [])
      );
      sessionStorage.setItem(
        "spare_parts_data",
        JSON.stringify(sparePartsData || [])
      );
      sessionStorage.setItem("tools_data", JSON.stringify(toolsData || []));
      sessionStorage.setItem("ppe_data", JSON.stringify(ppeData || []));
      sessionStorage.setItem(
        "maintenance_windows",
        JSON.stringify(windowsData || [])
      );
    } catch (err) {
      console.error("Error loading master data:", err);
      const msg = "Failed to load master data. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const loadWorkOrder = async () => {
    try {
      setLoading(true);
      const workOrder = await workOrderService.getWorkOrderById(
        parseInt(id, 10)
      );

      if (workOrder) {
        // Parse JSON fields
        const assignedTechIds = workOrder.assignedTechnicianIdsJson
          ? JSON.parse(workOrder.assignedTechnicianIdsJson)
          : [];
        const sparePartsList = workOrder.sparePartsJson
          ? JSON.parse(workOrder.sparePartsJson)
          : [];
        const toolsList = workOrder.toolsRequiredJson
          ? JSON.parse(workOrder.toolsRequiredJson)
          : [];
        const ppeList = workOrder.ppeRequiredJson
          ? JSON.parse(workOrder.ppeRequiredJson)
          : [];

        const loadedFormData = {
          workOrderNumber: workOrder.workOrderNumber || "",
          statusId: workOrder.statusId || "",
          priorityId: workOrder.priorityId || "",
          workTypeId: workOrder.workTypeId || "",
          machineId: workOrder.machineId ? workOrder.machineId.toString() : "",
          plantId: workOrder.plantId ? workOrder.plantId.toString() : "",
          lineId: workOrder.lineId ? workOrder.lineId.toString() : "",
          equipmentCode: workOrder.equipmentCode || "",
          assetCategory: workOrder.assetCategory || "",
          serialNumber: workOrder.serialNumber || "",
          problemDescription: workOrder.problemDescription || "",
          taskDescription: workOrder.taskDescription || "",
          rootCauseCategoryId: workOrder.rootCauseCategoryId || "",
          failureModeId: workOrder.failureModeId || "",
          attachmentsJson: workOrder.attachmentsJson || "",
          scheduledStartDate: workOrder.scheduledStartDate
            ? new Date(workOrder.scheduledStartDate).toISOString().split("T")[0]
            : "",
          scheduledEndDate: workOrder.scheduledEndDate
            ? new Date(workOrder.scheduledEndDate).toISOString().split("T")[0]
            : "",
          scheduledStartTime: workOrder.scheduledStartTime
            ? new Date(workOrder.scheduledStartTime).toISOString().slice(0, 16)
            : "",
          scheduledEndTime: workOrder.scheduledEndTime
            ? new Date(workOrder.scheduledEndTime).toISOString().slice(0, 16)
            : "",
          estimatedDurationHours: workOrder.estimatedDurationHours || "",
          maintenanceWindowId: workOrder.maintenanceWindowId || "",
          isPlanned: workOrder.isPlanned || false,
          assignedTechnicianIds: assignedTechIds,
          skillCategory: workOrder.skillCategory || "",
          spareParts: sparePartsList,
          toolsRequired: toolsList,
          ppeRequired: ppeList,
          lockoutTagoutRequired: workOrder.lockoutTagoutRequired || false,
          safetyInstructions: workOrder.safetyInstructions || "",
          riskLevel: workOrder.riskLevel || "",
          actualStartDate: workOrder.actualStartDate
            ? new Date(workOrder.actualStartDate).toISOString().split("T")[0]
            : "",
          actualEndDate: workOrder.actualEndDate
            ? new Date(workOrder.actualEndDate).toISOString().split("T")[0]
            : "",
          actualStartTime: workOrder.actualStartTime
            ? new Date(workOrder.actualStartTime).toISOString().slice(0, 16)
            : "",
          actualEndTime: workOrder.actualEndTime
            ? new Date(workOrder.actualEndTime).toISOString().slice(0, 16)
            : "",
          actualDurationHours: workOrder.actualDurationHours || "",
          downtimeMinutes: workOrder.downtimeMinutes || "",
          technicianNotes: workOrder.technicianNotes || "",
          observations: workOrder.observations || "",
          additionalIssues: workOrder.additionalIssues || "",
          readingsJson: workOrder.readingsJson || "",
          laborHours: workOrder.laborHours || "",
          laborCost: workOrder.laborCost || "",
          partsCost: workOrder.partsCost || "",
          otherCost: workOrder.otherCost || "",
          totalCost: workOrder.totalCost || "",
          completedBy: workOrder.completedBy || "",
          technicianSignature: workOrder.technicianSignature || "",
          verifiedBy: workOrder.verifiedBy || "",
          closedDate: workOrder.closedDate
            ? new Date(workOrder.closedDate).toISOString().split("T")[0]
            : "",
          maintenanceCode: workOrder.maintenanceCode || "",
          title: workOrder.title || "",
          description: workOrder.description || "",
          requestedBy: workOrder.requestedBy || currentUser?.userId || 0,
          assignedTo: workOrder.assignedTo || "",
          priority: workOrder.priority || "Medium",
          workOrderType: workOrder.workOrderType || "Breakdown",
          breakdownLogId: workOrder.breakdownLogId || null,
        };

        // Store the loaded data for merging during saves
        setExistingWorkOrderData(loadedFormData);
        setFormData(loadedFormData);

        // Determine current step based on status
        determineCurrentStep(workOrder.statusId, workOrder);
      }
    } catch (err) {
      console.error("Error loading work order:", err);
      const msg = "Failed to load work order. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const loadBreakdownData = async () => {
    // Pre-fill from breakdown log if available
    // This would require breakdownLogService
  };

  const determineCurrentStep = (statusId, workOrder) => {
    // Map status to step based on workflow
    const status = statuses.find((s) => s.statusId === statusId);
    if (!status) {
      setCurrentStep(1);
      return;
    }

    const statusName = status.statusName?.toLowerCase() || "";
    if (statusName.includes("draft")) {
      setCurrentStep(1);
    } else if (statusName.includes("open")) {
      setCurrentStep(2);
    } else if (statusName.includes("progress")) {
      setCurrentStep(5);
    } else if (statusName.includes("complete")) {
      setCurrentStep(8);
    } else if (statusName.includes("closed")) {
      setCurrentStep(9);
    } else {
      setCurrentStep(1);
    }
  };

  const validateStep = (step) => {
    const errors = {};

    switch (step) {
      case 1:
        if (!formData.statusId) errors.statusId = "Status is required";
        if (!formData.workTypeId) errors.workTypeId = "Work Type is required";
        if (!formData.priorityId) errors.priorityId = "Priority is required";
        break;
      case 2:
        if (!formData.machineId) errors.machineId = "Machine is required";
        break;
      case 3:
        if (!formData.problemDescription && !formData.taskDescription) {
          errors.problemDescription = "Problem or Task Description is required";
        }
        break;
      // Add more validations as needed
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < TOTAL_STEPS) {
        setCurrentStep(currentStep + 1);
        setCompletedSteps((prev) => new Set([...prev, currentStep]));
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (step) => {
    // Only allow navigation to completed steps or next step
    if (step <= currentStep || completedSteps.has(step)) {
      setCurrentStep(step);
    }
  };

  // Helper function to safely parse date
  const safeDateParse = (dateValue) => {
    if (!dateValue || dateValue === "") return null;
    try {
      const date = new Date(dateValue);
      return isNaN(date.getTime()) ? null : date.toISOString();
    } catch {
      return null;
    }
  };

  // Helper function to safely parse number
  const safeParseInt = (value) => {
    if (!value || value === "") return null;
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? null : parsed;
  };

  // Helper function to safely parse float
  const safeParseFloat = (value) => {
    if (!value || value === "") return null;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : parsed;
  };

  const handleSave = async (isDraft = false) => {
    try {
      setSaving(true);
      setError("");

      // Validate required fields
      if (!formData.machineId) {
        setError("Machine is required. Please go back to Step 2: Equipment.");
        setSaving(false);
        return;
      }

      // When in edit mode and saving a draft, merge with existing data to preserve fields from steps not yet visited
      let dataToSave = { ...formData };
      if (isEditMode && isDraft && existingWorkOrderData) {
        // For draft saves in edit mode, preserve existing values for fields that are empty/null in current formData
        // This prevents data loss when saving from an earlier step
        const fieldsToPreserve = [
          // Step 4: Scheduling
          "scheduledStartDate",
          "scheduledEndDate",
          "scheduledStartTime",
          "scheduledEndTime",
          "estimatedDurationHours",
          "maintenanceWindowId",
          "isPlanned",
          // Step 5: Resources
          "assignedTechnicianIds",
          "skillCategory",
          "spareParts",
          "toolsRequired",
          "ppeRequired",
          // Step 6: Safety
          "lockoutTagoutRequired",
          "safetyInstructions",
          "riskLevel",
          // Step 7: Execution
          "actualStartDate",
          "actualEndDate",
          "actualStartTime",
          "actualEndTime",
          "actualDurationHours",
          "downtimeMinutes",
          "technicianNotes",
          "observations",
          "additionalIssues",
          "readingsJson",
          // Step 8: Costs
          "laborHours",
          "laborCost",
          "partsCost",
          "otherCost",
          "totalCost",
          // Step 9: Completion
          "completedBy",
          "technicianSignature",
          "verifiedBy",
          "closedDate",
          "maintenanceCode",
        ];

        fieldsToPreserve.forEach(field => {
          // If current formData has empty/null value but existing data has a value, preserve the existing value
          const currentValue = dataToSave[field];
          const existingValue = existingWorkOrderData[field];

          if ((currentValue === null || currentValue === "" ||
            (Array.isArray(currentValue) && currentValue.length === 0) ||
            (typeof currentValue === 'object' && Object.keys(currentValue).length === 0)) &&
            existingValue !== null && existingValue !== "" &&
            !(Array.isArray(existingValue) && existingValue.length === 0)) {
            dataToSave[field] = existingValue;
          }
        });
      }

      // Prepare payload with safe parsing
      const payload = {
        workOrderNumber: dataToSave.workOrderNumber || null,
        workOrderType: dataToSave.workOrderType || "Breakdown",
        machineId: safeParseInt(dataToSave.machineId),
        plantId: safeParseInt(dataToSave.plantId),
        lineId: safeParseInt(dataToSave.lineId),
        priority: dataToSave.priority || "Medium",
        priorityId: safeParseInt(dataToSave.priorityId),
        workTypeId: safeParseInt(dataToSave.workTypeId),
        title: dataToSave.title || dataToSave.problemDescription || "Work Order",
        description: dataToSave.description || dataToSave.taskDescription || null,
        requestedBy: dataToSave.requestedBy || currentUser?.userId || 0,
        assignedTo: safeParseInt(dataToSave.assignedTo),
        statusId: isDraft
          ? statuses.find((s) => s.statusName?.toLowerCase().includes("draft"))
            ?.statusId || safeParseInt(dataToSave.statusId)
          : safeParseInt(dataToSave.statusId),
        // Enhanced fields
        equipmentCode: dataToSave.equipmentCode || null,
        assetCategory: dataToSave.assetCategory || null,
        serialNumber: dataToSave.serialNumber || null,
        problemDescription: dataToSave.problemDescription || null,
        taskDescription: dataToSave.taskDescription || null,
        rootCauseCategoryId: safeParseInt(dataToSave.rootCauseCategoryId),
        failureModeId: safeParseInt(dataToSave.failureModeId),
        attachmentsJson: dataToSave.attachmentsJson || null,
        scheduledStartDate: safeDateParse(dataToSave.scheduledStartDate),
        scheduledEndDate: safeDateParse(dataToSave.scheduledEndDate),
        scheduledStartTime: safeDateParse(dataToSave.scheduledStartTime),
        scheduledEndTime: safeDateParse(dataToSave.scheduledEndTime),
        estimatedDurationHours: safeParseFloat(dataToSave.estimatedDurationHours),
        maintenanceWindowId: safeParseInt(dataToSave.maintenanceWindowId),
        isPlanned: dataToSave.isPlanned || false,
        assignedTechnicianIdsJson:
          dataToSave.assignedTechnicianIds &&
            dataToSave.assignedTechnicianIds.length > 0
            ? JSON.stringify(dataToSave.assignedTechnicianIds)
            : null,
        skillCategory: dataToSave.skillCategory || null,
        sparePartsJson:
          dataToSave.spareParts && dataToSave.spareParts.length > 0
            ? JSON.stringify(dataToSave.spareParts)
            : null,
        toolsRequiredJson:
          dataToSave.toolsRequired && dataToSave.toolsRequired.length > 0
            ? JSON.stringify(dataToSave.toolsRequired)
            : null,
        ppeRequiredJson:
          dataToSave.ppeRequired && dataToSave.ppeRequired.length > 0
            ? JSON.stringify(dataToSave.ppeRequired)
            : null,
        lockoutTagoutRequired: dataToSave.lockoutTagoutRequired || false,
        safetyInstructions: dataToSave.safetyInstructions || null,
        riskLevel: dataToSave.riskLevel || null,
        actualStartDate: safeDateParse(dataToSave.actualStartDate),
        actualEndDate: safeDateParse(dataToSave.actualEndDate),
        actualStartTime: safeDateParse(dataToSave.actualStartTime),
        actualEndTime: safeDateParse(dataToSave.actualEndTime),
        actualDurationHours: safeParseFloat(dataToSave.actualDurationHours),
        downtimeMinutes: safeParseInt(dataToSave.downtimeMinutes),
        technicianNotes: dataToSave.technicianNotes || null,
        observations: dataToSave.observations || null,
        additionalIssues: dataToSave.additionalIssues || null,
        readingsJson: dataToSave.readingsJson || null,
        laborHours: safeParseFloat(dataToSave.laborHours),
        laborCost: safeParseFloat(dataToSave.laborCost),
        partsCost: safeParseFloat(dataToSave.partsCost),
        otherCost: safeParseFloat(dataToSave.otherCost),
        completedBy: safeParseInt(dataToSave.completedBy),
        technicianSignature: dataToSave.technicianSignature || null,
        verifiedBy: safeParseInt(dataToSave.verifiedBy),
        closedDate: safeDateParse(dataToSave.closedDate),
        maintenanceCode: dataToSave.maintenanceCode || null,
        breakdownLogId: safeParseInt(dataToSave.breakdownLogId),
      };

      // Only remove undefined values, keep null and empty strings
      // This ensures all form data entered in steps is sent to the server
      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined) {
          delete payload[key];
        }
      });

      // Ensure critical fields are included even if empty
      // This prevents data loss when fields are optional but should still be sent
      if (!payload.machineId && dataToSave.machineId) {
        payload.machineId = safeParseInt(dataToSave.machineId);
      }

      // Log formData and payload for debugging
      console.log("Form Data:", dataToSave);
      console.log("Work Order Payload:", JSON.stringify(payload, null, 2));

      if (isEditMode) {
        await workOrderService.updateWorkOrder(parseInt(id, 10), payload);
        toast.success("Work order updated successfully!");
        setSuccess("Work order updated successfully!");
        // Update existingWorkOrderData with the merged data for subsequent saves
        if (isDraft) {
          setExistingWorkOrderData(dataToSave);
        }
      } else {
        if (dataToSave.breakdownLogId) {
          await workOrderService.createWorkOrderFromBreakdown(
            parseInt(dataToSave.breakdownLogId, 10),
            payload
          );
        } else {
          await workOrderService.createWorkOrder(payload);
        }
        toast.success("Work order created successfully!");
        setSuccess("Work order created successfully!");
      }

      // Only navigate away if not saving as draft (drafts should stay on the page)
      if (!isDraft) {
        setTimeout(() => {
          navigate("/home/work-order");
        }, 1500);
      } else {
        toast.info("Draft saved successfully!");
      }
    } catch (err) {
      console.error("Error saving work order:", err);
      // Extract specific validation errors if available
      let errorMessage = "An error occurred while saving the work order.";

      if (err.response?.data) {
        if (typeof err.response.data === "string") {
          errorMessage = err.response.data;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data.errors) {
          // Flatten validation errors
          errorMessage = Object.values(err.response.data.errors)
            .flat()
            .join(", ");
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const stepNames = [
    "Basic Info",
    "Equipment",
    "Description",
    "Scheduling",
    "Resources",
    "Safety",
    "Execution",
    "Costs",
    "Completion",
  ];

  if (loading) {
    return (
      <div className="pagecontainer">
        <PageLoading message="Loading work order wizard..." />
      </div>
    );
  }

  return (
    <div className="pagecontainer">
      <div className="page-header">
        <div className="header-content">
          <h1>{isEditMode ? "Edit Work Order" : "New Work Order"}</h1>
          <p>
            Step {currentStep} of {TOTAL_STEPS}: {stepNames[currentStep - 1]}
          </p>
        </div>
        <button
          className="btn-icon"
          onClick={() => navigate("/home/work-order")}
        >
          <HiOutlineXMark />
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="wizard-steps">
        {stepNames.map((name, index) => {
          const stepNum = index + 1;
          const isCompleted = completedSteps.has(stepNum);
          const isCurrent = currentStep === stepNum;
          const isAccessible = stepNum <= currentStep || isCompleted;

          return (
            <div
              key={stepNum}
              className={`step-indicator ${isCurrent ? "current" : ""} ${isCompleted ? "completed" : ""
                } ${!isAccessible ? "disabled" : ""}`}
              onClick={() => isAccessible && handleStepClick(stepNum)}
            >
              <div className="step-number">
                {isCompleted ? <HiOutlineCheckCircle /> : stepNum}
              </div>
              <div className="step-label">{name}</div>
            </div>
          );
        })}
      </div>

      <div className="wizard-content pad10 wizardform">
        {currentStep === 1 && (
          <WorkOrderStep1BasicInfo
            formData={formData}
            setFormData={setFormData}
            formErrors={formErrors}
            workOrderTypes={workOrderTypes}
            priorities={priorities}
            statuses={statuses}
          />
        )}
        {currentStep === 2 && (
          <WorkOrderStep2Equipment
            formData={formData}
            setFormData={setFormData}
            formErrors={formErrors}
            machines={machines}
            plants={plants}
            lines={lines}
          />
        )}
        {currentStep === 3 && (
          <WorkOrderStep3Description
            formData={formData}
            setFormData={setFormData}
            formErrors={formErrors}
            rootCauseCategories={rootCauseCategories}
            failureModes={failureModes}
          />
        )}
        {currentStep === 4 && (
          <WorkOrderStep4Scheduling
            formData={formData}
            setFormData={setFormData}
            formErrors={formErrors}
            maintenanceWindows={maintenanceWindows}
          />
        )}
        {currentStep === 5 && (
          <WorkOrderStep5Resources
            formData={formData}
            setFormData={setFormData}
            formErrors={formErrors}
            users={users}
            spareParts={spareParts}
            tools={tools}
            ppeList={ppeList}
          />
        )}
        {currentStep === 6 && (
          <WorkOrderStep6Safety
            formData={formData}
            setFormData={setFormData}
            formErrors={formErrors}
          />
        )}
        {currentStep === 7 && (
          <WorkOrderStep7Execution
            formData={formData}
            setFormData={setFormData}
            formErrors={formErrors}
          />
        )}
        {currentStep === 8 && (
          <WorkOrderStep8Costs
            formData={formData}
            setFormData={setFormData}
            formErrors={formErrors}
          />
        )}
        {currentStep === 9 && (
          <WorkOrderStep9Completion
            formData={formData}
            setFormData={setFormData}
            formErrors={formErrors}
            users={users}
          />
        )}
      </div>

      <div className="wizard-footer">
        <div className="footer-left">
          {currentStep > 1 && (
            <button
              className="btn-secondary btn"
              onClick={handlePrevious}
              disabled={saving}
            >
              <HiOutlineChevronLeft />
              Previous
            </button>
          )}
        </div>
        <div className="footer-center">
          <button
            className="btn-secondary btn"
            onClick={() => handleSave(true)}
            disabled={saving}
          >
            <HiOutlineClock />
            Save Draft
          </button>
        </div>
        <div className="footer-right">
          {currentStep < TOTAL_STEPS ? (
            <button
              className="btn-primary btn"
              onClick={handleNext}
              disabled={saving}
            >
              Next
              <HiOutlineChevronRight />
            </button>
          ) : (
            <button
              className="btn-primary btn"
              onClick={() => handleSave(false)}
              disabled={saving}
            >
              {saving ? "Saving..." : isEditMode ? "Update" : "Create"}
              <HiOutlineCheckCircle />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkOrderWizard;
